import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { formatDate, formatTime } from '@/lib/format';
import {
  hoursForDate,
  occursOnDay,
  type BoardDay,
  type BoardWeek,
  type DayHours,
} from '@/lib/dispatchBoard';
import { toIsoDate } from '@/lib/timezone';
import type { ConflictMap, JobConflict } from '@/lib/conflicts';
import { availabilityOn, isUnavailable, type TechnicianBoardData } from '@/lib/availability';
import { ConflictBadge, describeConflicts } from './ConflictBadge';
import { TechnicianTooltip } from './TechnicianTooltip';
import type { TechnicianDetailStatus, UserSummary } from '@/routes/dispatch/useDispatchBoard';
import { jobCount, type BoardItem } from '@/lib/boardItems';
import type { JobDragBinding } from './dragBinding';

/** The technician-name column, and the narrowest a day column may become. */
const NAME_COLUMN_REM = 10;
const DAY_COLUMN_MIN_REM = 9;

/**
 * The week view (M1-10): technicians down, days across.
 *
 * <b>Not seven day-grids side by side.</b> A week at the day view's resolution
 * is seven half-hour grids across 25 technicians — 175 columns, unreadable at
 * any width, and the AC's "density stays readable with a busy week" rules it
 * out directly. Turning the axes instead answers the question a week actually
 * gets asked: who is loaded which day. Each cell is a list of one-line chips,
 * so a technician with six jobs on Thursday is six short lines rather than a
 * column of mostly whitespace.
 *
 * Same principle as the day view, and for the same reasons: the grid is a
 * layout, not something to navigate cell by cell. The content is a real list
 * per technician per day, each list named with whose day it is, and every
 * status is a word rather than a colour. Assignment interactions are M1-11,
 * which will attach to these same cells.
 */
export function WeekBoard({
  week,
  technicians,
  jobs,
  businessHours,
  timeZone,
  dragBinding,
  conflicts,
  board,
  detailStatus,
  onSelectDay,
}: {
  week: BoardWeek;
  technicians: UserSummary[];
  /** One per visit, plus one per job with none (M2-52): see `boardItems`. */
  jobs: BoardItem[];
  businessHours: DayHours[];
  timeZone: string;
  dragBinding: JobDragBinding;
  conflicts: ConflictMap;
  /** Availability, licences and skills (M1-39). Undefined until the one board request lands. */
  board: TechnicianBoardData | undefined;
  /** Whether that request is still in flight or came back empty-handed (M1-39). */
  detailStatus: TechnicianDetailStatus;
  /** Following a day heading drops into that day's board — the usual drill-down. */
  onSelectDay: (isoDate: string) => void;
}) {
  const { t } = useTranslation();

  // A day column times seven, plus the technician name column. The whole thing
  // scrolls sideways inside its own container rather than compressing a busy
  // Thursday into an unreadable sliver.
  //
  // The minimum width is stated outright rather than left to `min-w-max`.
  // `min-width: max-content` on a grid whose columns are `1fr` resolves against
  // the widest content in every column at once, and that measurement escapes
  // the scroll container: with it, the whole PAGE scrolled 179px sideways even
  // though the board itself was correctly clipped and scrolling internally —
  // and neither `overflow: hidden` on the board nor `min-w-0` on its wrapper
  // suppressed it. Both numbers come from the same two constants as the
  // template above them, so the floor and the columns cannot drift apart.
  const gridStyle = {
    gridTemplateColumns: `${NAME_COLUMN_REM}rem repeat(7, minmax(${DAY_COLUMN_MIN_REM}rem, 1fr))`,
    minWidth: `${NAME_COLUMN_REM + 7 * DAY_COLUMN_MIN_REM}rem`,
  } as const;

  const closedDays = week.days.map((day) => hoursForDate(businessHours, day.date) === undefined);

  // ⚠️ Which row's tooltip is open, purely so that row's heading can be lifted
  // above the ones below it. Every `<h3>` here is a direct grid child carrying
  // the same `sticky left-0 z-10`, and `position: sticky` creates a stacking
  // context — so the panel's own `z-30` is scoped INSIDE its heading, and
  // against a sibling with the identical z-index, DOM ORDER decides. The next
  // technicians' headings are later siblings with an opaque background, and
  // they painted over the open panel across the whole 10rem name column:
  // measured with `elementFromPoint`, the left 147 of the panel's 266 pixels
  // reported another heading as topmost from 40px down, which is every word
  // in it except the person's name.
  //
  // This is the same trap the day board hit, one axis over, and it is the
  // reason that fix does not transfer: raising this heading's constant would
  // raise all of them together and change nothing. Only the one holding an
  // open panel can win, so the board has to know which that is.
  const [openPanelFor, setOpenPanelFor] = useState<string | null>(null);

  // The day the row header's panel actually reports — the FIRST of the seven,
  // which the panel now names rather than calling "this day".
  const firstDayLabel = formatDate(week.days[0]!.dayStart, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone,
  });

  return (
    <div
      role="region"
      aria-labelledby="dispatch-week-heading"
      className="overflow-x-auto rounded-lg border"
      data-testid="week-board"
      // WCAG 2.1.1. Tailwind's `overflow-x-auto` computes `overflow-y` to
      // `auto` as well, so this container CLIPS vertically — and the tooltip
      // panel opened from a row header is taller than the space below it, by
      // about 106px. A pointer can scroll to the rest; a keyboard could not.
      // Chrome only makes a scroller focusable by itself when it has NO
      // focusable children, and this one has nine, so the panel's content —
      // including the vehicle section and the maintenance warning — was simply
      // unreachable without a mouse.
      //
      // ⚠️ This is the minimal remedy, not the fix. Giving the panel a
      // flip-to-above strategy is the real answer and belongs to its own story
      // (see web/CONVENTIONS.md); this makes the content REACHABLE meanwhile,
      // which is the part that is a defect rather than a rough edge.
      //
      // ⚠️ A `<div role="region">` rather than M1-27's named `<section>`,
      // which computed the SAME role. The two are interchangeable to a screen
      // reader and not to jsx-a11y: `no-noninteractive-tabindex` reads the
      // `role` attribute and will not resolve an implicit one, so a named
      // `<section>` is flagged even with `region` on its allow-list. M1-27
      // silenced that inline; M1-42 had five more containers to fix and chose
      // the shape both rules accept — see eslint.config.js for the whole
      // argument, written once instead of six times.
      tabIndex={0}
    >
      <h2 id="dispatch-week-heading" className="sr-only">
        {t('dispatch.weekHeading')}
      </h2>

      <div className="grid" style={gridStyle}>
        {/* Header row. `role="presentation"` is deliberately absent: these are
            headings for the columns below, and a day the business is closed
            says so in words next to its date. */}
        <div className="sticky left-0 z-10 border-b border-r bg-[var(--color-background)] px-3 py-2 text-xs font-medium">
          {t('dispatch.technicianColumn')}
        </div>
        {week.days.map((day, index) => (
          <div
            key={toIsoDate(day.date)}
            className={cn(
              'border-b px-2 py-2 text-xs',
              index < 6 && 'border-r',
              closedDays[index] && 'bg-[var(--color-muted)]',
            )}
          >
            {/* Weekday and date as two stacked parts, each formatted on its
                own. Asking Intl for `{weekday:'short', day:'numeric'}` in one
                call renders "13 Sun" in en-US — it has no pattern for that
                partial combination and falls back to an order no calendar
                uses. Stacking them also reads better in a narrow column.
                The accessible name is the full date, because "Sun 13" out of
                the visual context of a week grid says very little. */}
            <button
              type="button"
              onClick={() => onSelectDay(toIsoDate(day.date))}
              aria-label={formatDate(day.dayStart, {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                timeZone,
              })}
              className="block rounded text-left font-medium underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
            >
              <span className="block" aria-hidden="true">
                {formatDate(day.dayStart, { weekday: 'short', timeZone })}
              </span>
              <span className="block text-sm" aria-hidden="true">
                {formatDate(day.dayStart, { day: 'numeric', timeZone })}
              </span>
            </button>
            {closedDays[index] ? (
              <span className="block text-[0.625rem] text-[var(--color-muted-foreground)]">
                {t('dispatch.closedShort')}
              </span>
            ) : null}
          </div>
        ))}

        {technicians.map((technician) => {
          const theirs = jobs.filter((job) => job.assignedUserId === technician.id);
          const headingId = `dispatch-week-tech-${technician.id}`;

          return (
            // A Fragment, not a wrapper with `display: contents`. The row's
            // cells have to be direct children of the grid, and `contents` is
            // the very thing web/CONVENTIONS.md warns strips list and table
            // semantics in some browsers — a Fragment emits no element at all,
            // so there is nothing to strip. The row is held together for
            // assistive technology by the heading plus each cell's own label
            // rather than by a box.
            <Fragment key={technician.id}>
              <h3
                id={headingId}
                className={cn(
                  'sticky left-0 z-10 flex flex-col justify-center border-b border-r bg-[var(--color-background)] px-3 py-2 last:border-b-0',
                  // Load-bearing, and only while this row's panel is open —
                  // see `openPanelFor` above for what it is fighting.
                  openPanelFor === technician.id && 'z-30',
                )}
              >
                <TechnicianTooltip
                  technicianId={technician.id}
                  name={technician.name}
                  summary={board?.details.get(technician.id)}
                  detailStatus={detailStatus}
                  // The week's tooltip reports the FIRST day it is showing.
                  // A single line cannot honestly summarise seven different
                  // days, and the week view's own greying is per cell, which
                  // is where the day-by-day answer actually belongs.
                  //
                  // ⚠️ Which is exactly why it has to NAME that day. This
                  // header spans seven columns, so "not scheduled to work this
                  // day" here points at nothing — and since weeks start on
                  // SUNDAY, the day it silently meant was the one an ordinary
                  // Monday-to-Friday technician is least likely to be working.
                  // Every such person's panel read as if they were off.
                  dayState={board && availabilityOn(board.availability, technician.id, week.days[0]!.date)}
                  dayLabel={firstDayLabel}
                  activePanelId={openPanelFor}
                  onOpenChange={(open) =>
                    setOpenPanelFor((current) =>
                      open ? technician.id : current === technician.id ? null : current,
                    )
                  }
                />
                <span className="text-[0.625rem] text-[var(--color-muted-foreground)]">
                  {/* Jobs, not cards: a three-day repipe is one job (M2-52). */}
                  {t('dispatch.jobCount', { count: jobCount(theirs) })}
                </span>
              </h3>

              {week.days.map((day, index) => {
                const onThisDay = theirs
                  .filter((job) => occursOnDay(job, day))
                  .sort((a, b) => (a.scheduledStart ?? '').localeCompare(b.scheduledStart ?? ''));

                const cellState = board && availabilityOn(board.availability, technician.id, day.date);
                const cellUnavailable = isUnavailable(cellState);

                return (
                  <div
                    key={toIsoDate(day.date)}
                    className={cn(
                      'border-b p-1 align-top',
                      index < 6 && 'border-r',
                      // Either the business is closed that day, or this
                      // technician is not working it. Both are the same
                      // already-verified `--color-muted` shade, and neither
                      // stops a drop.
                      (closedDays[index] || cellUnavailable) && 'bg-[var(--color-muted)]',
                    )}
                    data-testid={`week-cell-${technician.id}-${toIsoDate(day.date)}`}
                    data-unavailable={cellUnavailable ? 'true' : undefined}
                    // A drop here changes who and which day, and keeps the
                    // job's existing time of day — the week has no slots to
                    // land in, and inventing one would silently reschedule a
                    // 7am call to whenever the grid happened to start.
                    data-drop-target=""
                    data-drop-technician={technician.id}
                    data-drop-date={toIsoDate(day.date)}
                  >
                    {cellUnavailable ? (
                      // The shade is the glance; this is the meaning. A cell
                      // greyed with nothing said about it is colour-only
                      // information, which this file's own palette rule and
                      // M1-09's "status is a word" both refuse.
                      <span className="sr-only">
                        {t(
                          cellState?.availabilityState === 'TimeOff'
                            ? 'dispatch.availability.offOn'
                            : 'dispatch.availability.notScheduledOn',
                          {
                            name: technician.name,
                            day: formatDate(day.dayStart, {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric',
                              timeZone,
                            }),
                          },
                        )}
                      </span>
                    ) : null}
                    {onThisDay.length === 0 ? (
                      // Not an empty div: a screen reader moving cell by cell
                      // should be told the day is free, not silently skipped.
                      <span className="sr-only">
                        {t('dispatch.nothingScheduled', {
                          name: technician.name,
                          day: formatDate(day.dayStart, {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                            timeZone,
                          }),
                        })}
                      </span>
                    ) : (
                      <ul
                        // Named, because a list of three chips sitting in a
                        // grid cell says nothing on its own about whose day it
                        // is. Without the label, a screen reader reads the
                        // week as 175 unattributed lists.
                        aria-label={t('dispatch.cellLabel', {
                          name: technician.name,
                          day: formatDate(day.dayStart, {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                            timeZone,
                          }),
                        })}
                        className="space-y-1"
                      >
                        {onThisDay.map((job) => (
                          <li key={job.id}>
                            <WeekChip
                              job={job}
                              day={day}
                              timeZone={timeZone}
                              dragBinding={dragBinding}
                              conflicts={conflicts.get(job.id)}
                            />
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

/**
 * One card — a visit, since M2-52 — compressed to what fits on a week: when, and who for. The address
 * and the description are the day view's job — at this density they would push
 * a busy Thursday past a screen's height and cost the overview the week exists
 * to give.
 *
 * A job that began the day before shows when it ENDS, not when it started.
 * Printing the start time on both days it touches puts "9:00 PM" at the top of
 * Wednesday for a job that ran Tuesday night — the one arrangement that is
 * actively misleading rather than merely terse. Caught by looking at a real
 * overnight job in the browser.
 */
function WeekChip({
  job,
  day,
  timeZone,
  dragBinding,
  conflicts,
}: {
  job: BoardItem;
  day: BoardDay;
  timeZone: string;
  dragBinding: JobDragBinding;
  conflicts?: JobConflict[];
}) {
  const { t } = useTranslation();
  const isUrgent = job.priority === 'Emergency' || job.priority === 'High';
  const continuesFromEarlier =
    job.scheduledStart !== null && new Date(job.scheduledStart) < day.dayStart;
  // One day of a multi-day job (M2-52) — the same words as the day view's card.
  const visitLabel =
    job.visitCount > 1 ? t('dispatch.visitOf', { number: job.visitNumber, total: job.visitCount }) : null;
  // M2-21: the day view's bounced marker, in the same words (see `JobCard`).
  const bounced = job.emailBounced ? t('dispatch.emailBounced') : null;

  return (
    <button
      type="button"
      {...dragBinding.handlersFor(job.id)}
      aria-label={[
        t('dispatch.move.cardLabel', { customer: job.customerName }),
        visitLabel,
        bounced,
        describeConflicts(conflicts, t),
      ]
        .filter(Boolean)
        .join('. ')}
      className={cn(
        'w-full cursor-grab touch-none rounded border bg-[var(--color-card)] px-1.5 py-1 text-left text-[0.6875rem] leading-tight',
        'focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none',
        isUrgent && 'border-l-2 border-l-[var(--color-destructive)]',
        conflicts && conflicts.length > 0 && 'border-[var(--color-destructive)]',
        dragBinding.draggingCardId === job.id && 'pointer-events-none opacity-40',
      )}
      data-testid="week-chip"
    >
      <span className="block truncate font-medium">
        {continuesFromEarlier ? (
          <>
            <span className="sr-only">{t('dispatch.continuedFromEarlier')}</span>
            <span aria-hidden="true">↳ </span>
            {job.scheduledEnd ? (
              <time dateTime={job.scheduledEnd}>
                {t('dispatch.untilTime', { time: formatTime(job.scheduledEnd, timeZone) })}
              </time>
            ) : null}
          </>
        ) : job.scheduledStart ? (
          <time dateTime={job.scheduledStart}>{formatTime(job.scheduledStart, timeZone)}</time>
        ) : null}{' '}
        {job.customerName}
      </span>
      <span className="block truncate text-[var(--color-muted-foreground)]">
        {t(`common.jobStatus.${job.status}`, { defaultValue: job.status })}
        {isUrgent
          ? ` · ${t(`common.jobPriority.${job.priority}`, { defaultValue: job.priority })}`
          : ''}
        {visitLabel ? ` · ${visitLabel}` : ''}
      </span>
      {bounced ? (
        <span className="block truncate font-medium text-[var(--color-destructive)]" data-testid="bounced-chip">
          {bounced}
        </span>
      ) : null}
      {conflicts && conflicts.length > 0 ? <ConflictBadge conflicts={conflicts} compact /> : null}
    </button>
  );
}
